import { FormEvent, ReactNode, useState } from 'react';
import { api, ApiError, setToken } from '../lib/api';
import { useNova } from '../lib/store';
import type { AuthUser } from '../lib/types';
import { landingView } from '../App';

// Sync AI brand greens, as on the Sync AI Omni sign-in page; this screen is always light
const GREEN = '#1f8f6b';
const GREEN_DARK = '#0f6b4d';
const GREEN_DARKER = '#0a3d2c';
const BORDER = '#e3e7e5';
const MUTED = '#6b7873';
const NEGATIVE = '#d64545';

/** Icons around the core on the left panel: the channels Omni handles, at their angles round the circle */
const CHANNELS: { top: string; left: string; paths: ReactNode }[] = [
  { top: '4%', left: '46%', paths: <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" /> },
  { top: '20%', left: '78%', paths: <><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" /><path d="m21.854 2.147-10.94 10.939" /></> },
  { top: '50%', left: '90%', paths: <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" /> },
  { top: '80%', left: '78%', paths: <><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" /><rect x="2" y="4" width="20" height="16" rx="2" /></> },
  { top: '96%', left: '46%', paths: <><rect width="14" height="20" x="5" y="2" rx="2" ry="2" /><path d="M12 18h.01" /></> },
  { top: '80%', left: '14%', paths: <><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></> },
  { top: '50%', left: '2%', paths: <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" /> },
  { top: '20%', left: '14%', paths: <><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" /><path d="m21.854 2.147-10.94 10.939" /></> },
];

function Lucide({ size, children }: { size: number; children: ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}

/** The square letter marks of the Sync AI brand: "S" for Sync AI, "O" for Omni. */
export function Mark({ letter, size, radius, fontSize, dark = true }: { letter: string; size: number; radius: number; fontSize?: number; dark?: boolean }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center font-bold"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        fontSize,
        color: GREEN,
        background: dark ? GREEN_DARKER : '#0f2e22',
        border: dark ? undefined : `1px solid ${GREEN}`,
      }}
      aria-hidden
    >
      {letter}
    </span>
  );
}

/**
 * Sign-in, in the Sync AI look (as on the Sync AI Omni portal): a dark
 * green panel with the channels Omni handles, and the form on a white panel.
 * Phones get the form only.
 */
export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ token: string; user: AuthUser }>('POST', '/auth/login', { email, password });
      setToken(res.token);
      useNova.getState().set({ user: res.user, view: landingView(res.user.role) });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  const input =
    'w-full rounded-[6px] border bg-white py-[9px] pl-[10px] pr-[34px] text-[14px] leading-[normal] text-black outline-none transition placeholder:text-[#757575] focus:border-[#1f8f6b]';
  const label = 'mb-[6px] block text-[12px] font-semibold';

  return (
    <main className="flex h-dvh w-full text-[#1a1f1d]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      <section
        className="relative hidden flex-[1.1_1_0%] items-center justify-center overflow-hidden md:flex"
        style={{ background: 'radial-gradient(circle at 50% 65%, rgb(18, 61, 44) 0%, rgb(6, 20, 15) 70%)' }}
        aria-hidden
      >
        <div className="absolute left-12 top-10 flex items-center gap-[10px] text-[22px] font-bold text-white">
          <Mark letter="O" size={40} radius={8} dark={false} />
          Omni
        </div>
        <div className="relative flex h-[320px] w-[320px] items-center justify-center">
          <div
            className="h-[130px] w-[130px] rotate-45 rounded-[24px]"
            style={{ background: 'linear-gradient(145deg, rgb(31, 143, 107), rgb(10, 61, 44))', boxShadow: 'rgba(31, 143, 107, 0.5) 0px 0px 60px' }}
          />
          {CHANNELS.map((c, i) => (
            <div
              key={i}
              className="absolute flex h-[44px] w-[44px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[10px] border"
              style={{ top: c.top, left: c.left, color: GREEN, background: 'rgba(255, 255, 255, 0.06)', borderColor: 'rgba(255, 255, 255, 0.15)' }}
            >
              <Lucide size={18}>{c.paths}</Lucide>
            </div>
          ))}
        </div>
      </section>

      <section className="flex w-full shrink-0 flex-col bg-white px-12 py-7 leading-[normal] md:w-[460px]">
        <div className="flex items-center gap-2 text-[16px] font-bold">
          <Mark letter="S" size={26} radius={6} />
          SyncAI
        </div>

        <div className="flex flex-1 flex-col justify-center">
          <div className="mb-[18px] flex items-center gap-2 text-[15px] font-bold">
            <Mark letter="O" size={24} radius={6} fontSize={13} />
            Omni
          </div>
          <h1 className="mb-5 text-[16px] font-bold">Login to Sync AI</h1>
          <form onSubmit={submit}>
            <div className="mb-4">
              <label htmlFor="login-email" className={label} style={{ color: GREEN_DARK }}>
                Email
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="username"
                placeholder="you@company.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={input}
                style={{ borderColor: BORDER }}
              />
            </div>
            <div className="mb-5">
              <label htmlFor="login-password" className={label} style={{ color: GREEN_DARK }}>
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={shown ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={input}
                  style={{ borderColor: BORDER }}
                />
                <button
                  type="button"
                  onClick={() => setShown((v) => !v)}
                  aria-label={shown ? 'Hide password' : 'Show password'}
                  aria-pressed={shown}
                  className="absolute right-2 top-1/2 flex -translate-y-1/2"
                  style={{ color: MUTED }}
                >
                  <Lucide size={16}>
                    {shown ? (
                      <>
                        <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                        <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                        <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                        <path d="m2 2 20 20" />
                      </>
                    ) : (
                      <>
                        <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                        <circle cx="12" cy="12" r="3" />
                      </>
                    )}
                  </Lucide>
                </button>
              </div>
            </div>
            {error ? (
              <p role="alert" className="mb-3 text-[12px]" style={{ color: NEGATIVE }}>
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-[6px] bg-[#0f6b4d] py-[11px] text-[14px] font-semibold leading-[normal] text-white transition hover:bg-[#0a3d2c] disabled:bg-[#0a3d2c]"
            >
              {busy ? 'Logging in…' : 'Login'}
            </button>
          </form>
        </div>

        <p className="text-[11px]" style={{ color: MUTED }}>
          Powered by Sync AI {new Date().getFullYear()}. All rights reserved.
        </p>
      </section>
    </main>
  );
}

export function NovaMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden>
      <circle cx="20" cy="20" r="15" fill="none" stroke="var(--color-ion)" strokeWidth="2.5" opacity="0.35" />
      <path d="M20 5a15 15 0 0 1 15 15" fill="none" stroke="var(--color-ion)" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="20" cy="20" r="5.5" fill="var(--color-ion)" />
    </svg>
  );
}
